import { differenceInYears, format, intervalToDuration } from "date-fns";
import { enUS, ptBR } from "date-fns/locale";
import { escapeHtml } from "../layout/escapeHtml";
import type { PrintLayout } from "../layout/printLayout";
import { buildPosologySummary, isFreeTextPosologyItem, type PosologyItemFields } from "./posology";
import type { ManipulatedPayloadData } from "./manipulatedPayload";
import { getPrescriptionCopies } from "./getPrescriptionCopies";
import { formatBrazilianPhone, isMapaRegulatedPrescription, resolveMapaPresentation } from "./presentation";

type Lang = "pt-BR" | "en";
type Translate = (key: string, options?: Record<string, unknown>) => string;

export interface PrescriptionItemData extends PosologyItemFields {
  medicationName: string;
  concentration: string | null;
  presentation: string | null;
  quantity: string | null;
  pharmaceuticalForm: string | null;
  administrationRoute: string | null;
}

export interface BuyerData {
  name: string | null; document: string | null; address: string | null; cityUf: string | null; phone: string | null;
}

export interface PrescriptionDocumentData {
  type: string;
  issuedAt: string | null;
  validityInDays: number | null;
  observations: string | null;
  vetName: string;
  crmv: string;
  vetPhone?: string | null;
  vetAddress?: string | null;
  clinicName: string;
  controlRegime?: string | null;
  mapaNumber?: string | null;
  mapaRegistration?: string | null;
  mapaPrescriptionIdentifier?: string | null;
  manipulatedPayload?: ManipulatedPayloadData | null;
  buyer?: BuyerData | null;
  pet: {
    name: string; ownerName: string; ownerDocument?: string | null; ownerAddress?: string | null;
    species: string | null; breed: string | null; gender?: string | null; birthDate: string | null; weightKg: number | null;
  };
  items: PrescriptionItemData[];
  examItems: Array<{ examName: string; instructions: string | null }>;
}

export interface RenderPrescriptionOptions {
  signed: boolean;
  showIssuerBlock: boolean;
  stampShowCrmv: boolean;
  stampShowMapa: boolean;
  /** Preview only: marks the item being edited (index in the final list). Never set by the PDF Lambda. */
  draftHighlight?: { kind: "medication" | "formula" | "exam"; index: number } | null;
}

export const renderOptionsFromLayout = (layout: PrintLayout, signed: boolean): RenderPrescriptionOptions => ({
  signed, showIssuerBlock: layout.showIssuerBlock, stampShowCrmv: layout.stampShowCrmv, stampShowMapa: layout.stampShowMapa,
});

const DASH = "—";
const norm = (v: string | null | undefined) => (typeof v === "string" ? v.trim() : "");
const esc = (v: string | null | undefined) => escapeHtml(norm(v));
const escOrDash = (v: string | null | undefined) => (norm(v) ? escapeHtml(norm(v)) : DASH);
const locale = (lang: Lang) => (lang === "pt-BR" ? ptBR : enUS);
const longDate = (d: Date, lang: Lang) => format(d, lang === "pt-BR" ? "dd 'de' MMMM 'de' yyyy" : "MMMM dd, yyyy", { locale: locale(lang) });

function parseDateOnly(value: string): Date | null {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) { const [y, m, d] = value.split("-").map(Number); return new Date(y, m - 1, d); }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function ageLabel(birth: Date, lang: Lang): string {
  const now = new Date();
  const years = differenceInYears(now, birth);
  const pt = lang === "pt-BR";
  if (years >= 1) return pt ? `${years} ${years === 1 ? "ano" : "anos"}` : `${years} ${years === 1 ? "year" : "years"}`;
  const { months = 0, days = 0 } = intervalToDuration({ start: birth, end: now });
  if (months >= 1) return pt ? `${months} ${months === 1 ? "mês" : "meses"}` : `${months} ${months === 1 ? "month" : "months"}`;
  return pt ? `${days} ${days === 1 ? "dia" : "dias"}` : `${days} ${days === 1 ? "day" : "days"}`;
}

const STYLE = `<style>
.doc-title{display:flex;flex-direction:column;align-items:center;gap:3px}
.doc-title__main{font-weight:800;font-size:calc(15.5px*var(--doc-scale));letter-spacing:.08em;color:var(--doc-title);text-align:center}
.doc-title__via{font-size:calc(11px*var(--doc-scale));opacity:.75}
.doc-parties{border-top:1px solid #E3DDD5;border-bottom:1px solid #E3DDD5}
.doc-issuer{display:flex;flex-wrap:wrap;align-items:baseline;gap:4px 14px;padding:8px 2px;border-bottom:1px solid var(--doc-rule-soft);font-size:calc(11px*var(--doc-scale))}
.doc-label{font-weight:700;font-size:calc(9.5px*var(--doc-scale));letter-spacing:.12em;color:var(--doc-title)}
.doc-muted{opacity:.6}
.doc-people{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);column-gap:41px;padding:11px 2px 12px}
.doc-person{display:flex;flex-direction:column;gap:3px;min-width:0}
.doc-person__name{font-weight:700;font-size:calc(16px*var(--doc-scale));line-height:1.2}
.doc-person__line{font-size:calc(11.5px*var(--doc-scale));line-height:1.5}
.doc-items{display:flex;flex-direction:column;gap:calc(13px*var(--doc-scale));padding-top:2px}
.doc-section{display:flex;align-items:center;gap:10px}
.doc-section__title{font-weight:700;font-size:calc(12px*var(--doc-scale));letter-spacing:.06em;color:var(--doc-title)}
.doc-section__rule{flex:1;height:1px;background:var(--doc-rule)}
.doc-item{display:flex;flex-direction:column;gap:4px;break-inside:avoid}
.doc-item__row{display:flex;align-items:baseline;gap:8px;font-size:calc(13px*var(--doc-scale))}
.doc-leader{flex:1;border-bottom:1.5px dotted #B8B1A8;transform:translateY(-3px)}
.doc-item__posology{font-size:calc(12.5px*var(--doc-scale));line-height:calc(var(--doc-lh) + .05);padding-left:18px;white-space:pre-line}
.doc-boxes{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px;margin-top:6px}
.doc-box{background:var(--doc-tint);border-radius:8px;padding:7px 10px}
.doc-box__label{font-size:calc(9.5px*var(--doc-scale));opacity:.62}
.doc-box__value{font-weight:600;font-size:calc(12px*var(--doc-scale))}
.doc-note{font-size:calc(12px*var(--doc-scale));line-height:calc(var(--doc-lh) + .1);background:var(--doc-tint);border-radius:8px;padding:10px 12px;white-space:pre-line}
.doc-note b{color:var(--doc-title)}
.doc-sign-simple{display:flex;align-items:flex-end;justify-content:space-between;gap:24px;break-inside:avoid}
.doc-sign-simple__stamp{width:300px;display:flex;flex-direction:column;align-items:center;gap:2px;font-size:calc(11.5px*var(--doc-scale));text-align:center}
.doc-sign-line{width:100%;border-bottom:1px solid #1F1D1B}
.doc-sign-name{font-weight:700;font-size:calc(12.5px*var(--doc-scale));padding-top:6px}
.doc-sign-control{display:grid;grid-template-columns:170px minmax(0,1fr) minmax(0,.85fr);gap:14px;align-items:stretch;break-inside:avoid}
.doc-sign-control__date{font-weight:800;font-size:calc(12.5px*var(--doc-scale));color:var(--doc-title);text-transform:uppercase}
.doc-fillbox{border:1px solid var(--doc-box);border-radius:10px;padding:10px 12px;display:flex;flex-direction:column;gap:7px;font-size:calc(11px*var(--doc-scale))}
.doc-fillbox__title{font-size:calc(10.5px*var(--doc-scale));letter-spacing:.04em}
.doc-fill{display:flex;gap:6px;align-items:baseline}
.doc-fill__label{opacity:.7;white-space:nowrap}
.doc-fill__line{flex:1;border-bottom:1px solid #DDD7CF;min-height:14px;font-weight:600}
.doc-item--draft{outline:2px dashed #C9492A;outline-offset:6px;border-radius:8px;background:#FFF8F5;position:relative}
.doc-draft-badge{position:absolute;top:-11px;right:4px;font:700 10px Inter,sans-serif;color:#fff;background:#C9492A;border-radius:999px;padding:3px 8px}
.doc-fillbox__signature{border-top:1px solid #1F1D1B;padding-top:4px;font-size:calc(10px*var(--doc-scale));opacity:.75;margin-top:auto}
</style>`;

const isControlled = (d: PrescriptionDocumentData) => d.type === "controlled" || d.controlRegime === "controlled_human";

export function getPrescriptionViaNames(data: PrescriptionDocumentData, t: Translate): string[] {
  const copies = getPrescriptionCopies(data.controlRegime);
  if (copies.length <= 1) return copies.map(() => "");
  return copies.map((copy) => t(`print.vias.${copy.labelKey}`));
}

function titleFor(data: PrescriptionDocumentData, tp: Translate): string {
  if (data.type === "exam") return tp("examRequest");
  if (data.type === "manipulated") return tp("titleManipulated");
  if (isControlled(data)) return tp("titleControlled");
  return tp("title");
}

function viaLine(data: PrescriptionDocumentData, viaIndex: number, viaName: string, tp: Translate): string {
  if (!viaName) return "";
  const labelKey = getPrescriptionCopies(data.controlRegime)[viaIndex]?.labelKey;
  const ordinal = tp(`viaOrdinal${viaIndex + 1}`, { defaultValue: "" });
  const purpose = labelKey ? tp(`viaPurpose${labelKey[0].toUpperCase()}${labelKey.slice(1)}`, { defaultValue: "" }) : "";
  return ordinal && purpose ? `${ordinal} — ${purpose}` : viaName;
}

function partiesHtml(data: PrescriptionDocumentData, tp: Translate, lang: Lang, opts: RenderPrescriptionOptions): string {
  const pet = data.pet;
  const mapa = resolveMapaPresentation({ controlRegime: data.controlRegime, snapshotRegistration: data.mapaRegistration, snapshotIdentifier: data.mapaPrescriptionIdentifier, legacyMapaNumber: data.mapaNumber });
  const birth = pet.birthDate ? parseDateOnly(pet.birthDate) : null;
  const weight = pet.weightKg != null && !Number.isNaN(Number(pet.weightKg))
    ? tp("weightKilograms", { weight: new Intl.NumberFormat(lang, { maximumFractionDigits: 2 }).format(Number(pet.weightKg)) })
    : DASH;
  const ageLine = birth
    ? `${escapeHtml(ageLabel(birth, lang))} <span class="doc-muted">(${escapeHtml(tp("bornShort", { date: format(birth, "dd/MM/yyyy") }))})</span>`
    : DASH;
  const issuer = opts.showIssuerBlock
    ? `<div class="doc-issuer">
        <span class="doc-label">${escapeHtml(tp("issuerLabel"))}</span>
        <span style="font-weight:600">${escOrDash(data.vetName)}</span>
        ${norm(data.crmv) ? `<span><span class="doc-muted">${escapeHtml(tp("crmvShort"))}</span> ${esc(data.crmv.replace(/^CRMV[-\s]?/i, ""))}</span>` : ""}
        ${norm(mapa.registration) ? `<span><span class="doc-muted">${escapeHtml(tp("mapaShort"))}</span> ${esc(mapa.registration)}</span>` : ""}
        ${norm(data.clinicName) ? `<span>${esc(data.clinicName)}</span>` : ""}
        ${formatBrazilianPhone(data.vetPhone) ? `<span>${escapeHtml(formatBrazilianPhone(data.vetPhone))}</span>` : ""}
        ${norm(data.vetAddress) ? `<span>${esc(data.vetAddress)}</span>` : ""}
      </div>`
    : "";
  return `<div class="doc-parties">${issuer}
    <div class="doc-people">
      <div class="doc-person">
        <span class="doc-label">${escapeHtml(tp("patientLabel"))}</span>
        <span class="doc-person__name">${escOrDash(pet.name)}</span>
        <span class="doc-person__line">${escOrDash(pet.species)} · ${escOrDash(pet.breed)} · ${escOrDash(pet.gender)}</span>
        <span class="doc-person__line">${ageLine} · ${escapeHtml(weight)}</span>
      </div>
      <div class="doc-person">
        <span class="doc-label">${escapeHtml(tp("ownerLabel"))}</span>
        <span class="doc-person__name">${escOrDash(pet.ownerName)}</span>
        <span class="doc-person__line"><span class="doc-muted">${escapeHtml(tp("cpfLabel"))}</span> ${escOrDash(pet.ownerDocument)}</span>
        <span class="doc-person__line">${escOrDash(pet.ownerAddress)}</span>
      </div>
    </div>
  </div>`;
}

const section = (title: string) =>
  `<div class="doc-section"><div class="doc-section__title">${escapeHtml(title)}</div><div class="doc-section__rule"></div></div>`;

const draftBadge = (tp: Translate) => `<span class="doc-draft-badge">${escapeHtml(tp("draftBadge"))}</span>`;

function medicationItemsHtml(data: PrescriptionDocumentData, tp: Translate, lang: Lang, opts: RenderPrescriptionOptions): string {
  const groups = new Map<string, Array<{ item: PrescriptionItemData; index: number }>>();
  data.items.forEach((item, index) => {
    const key = norm(item.administrationRoute).toUpperCase();
    groups.set(key, [...(groups.get(key) ?? []), { item, index }]);
  });
  const highlight = opts.draftHighlight?.kind === "medication" ? opts.draftHighlight.index : -1;
  let n = 0;
  return [...groups.entries()].map(([route, items]) => {
    const head = section(route ? tp("routeSection", { route }) : tp("prescriptionSection"));
    const rows = items.map(({ item, index }) => {
      n += 1;
      const posology = isFreeTextPosologyItem(item)
        ? norm(item.free_text_posology) || buildPosologySummary(item, tp, lang === "pt-BR" ? "pt" : "en")
        : buildPosologySummary(item, tp, lang === "pt-BR" ? "pt" : "en");
      const qty = norm(item.quantity);
      const draft = index === highlight;
      return `<div class="doc-item${draft ? " doc-item--draft" : ""}">${draft ? draftBadge(tp) : ""}
        <div class="doc-item__row"><span style="font-weight:700">${n}.</span><span style="font-weight:600">${escOrDash(item.medicationName)}</span>${qty ? `<span class="doc-leader"></span><span style="font-weight:600">${escapeHtml(qty)}</span>` : ""}</div>
        ${posology ? `<div class="doc-item__posology">${escapeHtml(posology)}</div>` : ""}
      </div>`;
    }).join("");
    return head + rows;
  }).join("");
}

function manipulatedHtml(payload: ManipulatedPayloadData, tp: Translate, opts: RenderPrescriptionOptions): string {
  const draft = opts.draftHighlight?.kind === "formula";
  const wrap = (inner: string) => (draft ? `<div class="doc-item doc-item--draft">${draftBadge(tp)}${inner}</div>` : inner);
  const route = norm(payload.route);
  if (payload.freeText) {
    return wrap(`${section(tp("routeSection", { route: route ? route.toUpperCase() : tp("prescriptionSection") }))}
      <div style="font-size:calc(13px*var(--doc-scale));line-height:1.6;white-space:pre-line">${escapeHtml(payload.freeText)}</div>`);
  }
  const form = norm(payload.pharmaceuticalForm)
    ? `<div style="font-size:calc(13px*var(--doc-scale))"><b>${esc(payload.pharmaceuticalForm)}</b> <span style="opacity:.7">— ${escapeHtml(tp("pharmaceuticalFormSuffix"))}</span></div>` : "";
  const components = payload.components.map((c) => `${norm(c.name) || norm(c.concentration) ? `<div class="doc-item__row">
      <span style="font-weight:600">${escOrDash(c.name)}</span>
      ${norm(c.concentration) ? `<span class="doc-leader"></span><span style="font-weight:600">${esc(c.concentration)}</span>` : ""}
    </div>` : ""}${c.freeText ? `<div class="doc-item__posology"${norm(c.name) ? "" : ' style="padding-left:0"'}>${escapeHtml(c.freeText)}</div>` : ""}`).join("");
  const box = (label: string, value: string) => `<div class="doc-box"><div class="doc-box__label">${escapeHtml(label)}</div><div class="doc-box__value">${value ? escapeHtml(value) : DASH}</div></div>`;
  return wrap(`${section(tp("routeSection", { route: route ? route.toUpperCase() : tp("prescriptionSection") }))}
    <div style="display:flex;flex-direction:column;gap:6px">
      ${form}
      <div style="font-weight:600;font-size:calc(11px*var(--doc-scale));opacity:.7;padding-top:4px">${escapeHtml(tp("compositionPerDose"))}</div>
      ${components}
      <div class="doc-boxes">
        ${box(tp("routeBox"), route)}
        ${box(tp("treatmentDaysBox"), norm(payload.treatmentDays) ? tp("daysValue", { count: payload.treatmentDays }) : "")}
        ${box(tp("totalDosesBox"), norm(payload.totalDoses) ? tp("dosesValue", { count: payload.totalDoses }) : "")}
      </div>
      ${norm(payload.instructions) ? `<div class="doc-item__posology" style="padding-left:0;padding-top:4px"><b>${escapeHtml(tp("instructionsLabel"))}</b> ${esc(payload.instructions)}</div>` : ""}
    </div>`);
}

function examsHtml(data: PrescriptionDocumentData, tp: Translate, opts: RenderPrescriptionOptions): string {
  return section(tp("exams").replace(/:$/, "").toUpperCase()) + data.examItems.map((e, i) => `<div class="doc-item${opts.draftHighlight?.kind === "exam" && opts.draftHighlight.index === i ? " doc-item--draft" : ""}">${opts.draftHighlight?.kind === "exam" && opts.draftHighlight.index === i ? draftBadge(tp) : ""}
    <div style="font-weight:600;font-size:calc(13px*var(--doc-scale))">${i + 1}. ${escOrDash(e.examName)}</div>
    ${norm(e.instructions) ? `<div style="font-size:calc(12px*var(--doc-scale));padding-left:16px;opacity:.85"><span style="font-weight:500">${escapeHtml(tp("instructions"))}: </span>${esc(e.instructions)}</div>` : ""}
  </div>`).join("");
}

function signatureHtml(data: PrescriptionDocumentData, tp: Translate, lang: Lang, opts: RenderPrescriptionOptions): string {
  const mapa = resolveMapaPresentation({ controlRegime: data.controlRegime, snapshotRegistration: data.mapaRegistration, snapshotIdentifier: data.mapaPrescriptionIdentifier, legacyMapaNumber: data.mapaNumber });
  const issued = data.issuedAt ? parseDateOnly(data.issuedAt) : null;
  const dateText = issued ? longDate(issued, lang) : tp("noDate");
  const crmv = opts.stampShowCrmv && norm(data.crmv) ? esc(data.crmv) : "";
  // MAPA-regulated prescriptions always carry the sequence number, regardless of the stamp layout option.
  const mapaValue = opts.stampShowMapa || isMapaRegulatedPrescription(data.controlRegime) ? norm(mapa.identifier) || norm(mapa.registration) : "";
  if (isControlled(data)) {
    const buyer = data.buyer;
    const fill = (label: string, value: string | null | undefined) =>
      `<div class="doc-fill"><span class="doc-fill__label">${escapeHtml(label)}</span><span class="doc-fill__line">${esc(value)}</span></div>`;
    return `<div class="doc-sign-control">
      <div style="display:flex;flex-direction:column;gap:4px;font-size:calc(11.5px*var(--doc-scale));line-height:1.4">
        <div class="doc-sign-control__date">${escapeHtml(dateText)}</div>
        ${opts.signed ? "" : `<div class="doc-sign-line" style="height:34px;margin-top:6px"></div>`}
        <div class="doc-sign-name" style="font-size:calc(12px*var(--doc-scale))">${escOrDash(data.vetName)}</div>
        ${crmv ? `<div>${crmv}</div>` : ""}
        ${mapaValue ? `<div>${escapeHtml(tp("mapaRegistryLine", { value: mapaValue }))}</div>` : ""}
      </div>
      <div class="doc-fillbox">
        <div class="doc-fillbox__title">${tp("buyerTitle")}</div>
        ${fill(tp("buyerName"), buyer?.name)}${fill(tp("buyerDocument"), buyer?.document)}${fill(tp("buyerAddress"), buyer?.address)}${fill(tp("buyerCity"), buyer?.cityUf)}${fill(tp("buyerPhone"), buyer?.phone)}
      </div>
      <div class="doc-fillbox">
        <div class="doc-fillbox__title">${tp("supplierTitle")}</div>
        ${fill(tp("supplierDate"), null)}
        <div class="doc-fillbox__signature">${escapeHtml(tp("pharmacistSignature"))}</div>
      </div>
    </div>`;
  }
  let validity = "";
  if (issued && data.validityInDays) {
    const until = new Date(issued); until.setDate(until.getDate() + data.validityInDays);
    validity = ` · ${escapeHtml(tp("validUntilShort", { date: longDate(until, lang) }))}`;
  }
  const stampLine = [crmv, mapaValue ? escapeHtml(tp("mapaRegistryLine", { value: mapaValue })) : ""].filter(Boolean).join(" · ");
  return `<div class="doc-sign-simple">
    <div style="font-size:calc(12px*var(--doc-scale));padding-bottom:6px">${escapeHtml(dateText)}${validity}</div>
    <div class="doc-sign-simple__stamp">
      ${opts.signed ? "" : `<div class="doc-sign-line" style="height:40px"></div>`}
      <div class="doc-sign-name">${escOrDash(data.vetName)}</div>
      ${stampLine ? `<div>${stampLine}</div>` : ""}
    </div>
  </div>`;
}

export function renderPrescriptionVia(data: PrescriptionDocumentData, viaIndex: number, t: Translate, lang: Lang, opts: RenderPrescriptionOptions): string {
  const tp = (key: string, o?: Record<string, unknown>) => t(`print.${key}`, o);
  const viaName = getPrescriptionViaNames(data, t)[viaIndex] ?? "";
  const items = data.type === "manipulated" && data.manipulatedPayload
    ? manipulatedHtml(data.manipulatedPayload, tp, opts)
    : data.type === "exam" ? examsHtml(data, tp, opts) : medicationItemsHtml(data, tp, lang, opts);
  const note = norm(data.observations)
    ? `<div class="doc-note"><b>${escapeHtml(tp(data.type === "exam" ? "guidanceExamLabel" : "guidanceLabel"))}</b> ${esc(data.observations)}</div>` : "";
  const via = viaLine(data, viaIndex, viaName, tp);
  const mapaSequence = isMapaRegulatedPrescription(data.controlRegime)
    ? norm(resolveMapaPresentation({ controlRegime: data.controlRegime, snapshotRegistration: data.mapaRegistration, snapshotIdentifier: data.mapaPrescriptionIdentifier, legacyMapaNumber: data.mapaNumber }).identifier)
    : "";
  return `${STYLE}
  <section data-zone="body" style="display:flex;flex-direction:column;gap:calc(12px*var(--doc-scale))">
    <div class="doc-title"><div class="doc-title__main">${escapeHtml(titleFor(data, tp))}</div>${via ? `<div class="doc-title__via">${escapeHtml(via)}</div>` : ""}${mapaSequence ? `<div class="doc-title__via" style="opacity:1">${tp("mapaSequenceLine", { value: escapeHtml(mapaSequence) })}</div>` : ""}</div>
    ${partiesHtml(data, tp, lang, opts)}
    <div class="doc-items">${items}</div>
    ${note}
  </section>
  <div class="doc-spacer"></div>
  <section data-zone="sign">${signatureHtml(data, tp, lang, opts)}</section>`;
}

export function renderPrescriptionBody(
  data: PrescriptionDocumentData,
  t: Translate,
  lang: Lang,
  opts: RenderPrescriptionOptions,
): string {
  const names = getPrescriptionViaNames(data, t);
  return names.map((_n, i) => renderPrescriptionVia(data, i, t, lang, opts)
    + (i < names.length - 1 ? '<div style="page-break-after:always"></div>' : "")).join("");
}
